import { EWorkflowStatusDataBlock } from "constants/index";
import { FeatureTypes } from "entity-app/constants";
import { TEntityLink, TFeatureSpecSection, TLinkSource } from "../models";

import { getLinkedWithStartdate } from ".";

type TInputJSON = {
  id: string;
  auto_inc_value: number;
  title: string;
  feature_type_id: FeatureTypes;
  feature_instance_spec_sections: TFeatureSpecSection[];
  feature_workflow_instance: {
    workflow_status: EWorkflowStatusDataBlock;
  };
  feature_submittals: { material_tracking: boolean }[];
  links_as_source: any[];
  links_as_target: any[];
};

const mergeLinksRecursive = (obj: TInputJSON): TEntityLink => {
  if (!obj || typeof obj !== "object") return obj;

  // Create a shallow copy to avoid mutating the parameter
  const objCopy = { ...obj } as TInputJSON;

  // Merge links_as_source → feature_instance
  const sourceLinks: TLinkSource[] =
    objCopy.links_as_source?.map((link) => {
      const featureInstance = mergeLinksRecursive(
        link.target_feature_instance
      ) as any;
      return {
        governing_link: link.governing_link,
        created_at: link.created_at,
        link_type: link.link_type,
        offset_duration: link.offset_duration,
        linked_offset_date: link.linked_offset_date,
        reverse_dependency: link.reverse_dependency,
        feature_instance: featureInstance,
        isLinkedWithStartdate: getLinkedWithStartdate(
          link.link_type,
          link.reverse_dependency
        ),
        featureInstanceId: featureInstance.id
      } as TLinkSource;
    }) ?? [];

  // Merge links_as_target → feature_instance
  const targetLinks: TLinkSource[] =
    objCopy.links_as_target?.map((link: any) => {
      const featureInstance = mergeLinksRecursive(
        link.source_feature_instance
      ) as any;
      return {
        governing_link: link.governing_link,
        created_at: link.created_at,
        link_type: link.link_type,
        offset_duration: link.offset_duration,
        linked_offset_date: link.linked_offset_date,
        reverse_dependency: link.reverse_dependency,
        feature_instance: featureInstance,
        isLinkedWithStartdate: getLinkedWithStartdate(
          link.link_type,
          link.reverse_dependency
        ),
        featureInstanceId: featureInstance.id
      } as TLinkSource;
    }) ?? [];

  // Remove old keys so result is clean
  // delete objCopy.links_as_source;
  // delete objCopy.links_as_target;

  // Copy other fields
  const merged = {
    ...objCopy,
    ids: [objCopy.id],
    links: [...sourceLinks, ...targetLinks]
  };

  return merged;
};

const findColumnDataOfFeature = (features: TInputJSON[]): TInputJSON | null => {
  if (features.length === 0) return null;
  if (features.length === 1) return structuredClone(features[0]);

  const first = features[0];

  const merged: TInputJSON = {
    id: features.length === 1 ? first.id : "",
    auto_inc_value: first.auto_inc_value,
    title: first.title,
    feature_type_id: first.feature_type_id,
    feature_instance_spec_sections: structuredClone(
      first.feature_instance_spec_sections
    ),
    feature_workflow_instance: { ...first.feature_workflow_instance },
    feature_submittals: structuredClone(first.feature_submittals),
    links_as_source: structuredClone(first.links_as_source),
    links_as_target: structuredClone(first.links_as_target)
  };

  // If titles differ → clear
  if (!features.every((f) => f.title === merged.title)) {
    merged.title = "";
  }

  // If feature_type_id might differ in future
  if (!features.every((f) => f.feature_type_id === merged.feature_type_id)) {
    // @ts-expect-error sentinel if mismatch
    merged.feature_type_id = null;
  }

  // Common spec sections (intersection by section_number)
  const specNumbers = features.map(
    (f) =>
      new Set(
        f.feature_instance_spec_sections.map(
          (s) => s.spec_section.section_number
        )
      )
  );
  merged.feature_instance_spec_sections =
    merged.feature_instance_spec_sections.filter((s) =>
      specNumbers.every((set) => set.has(s.spec_section.section_number))
    );

  // Handle feature_submittals
  if (features.some((f) => f.feature_submittals.length === 0)) {
    merged.feature_submittals = [];
  } else {
    const allTrue = features.every((f) =>
      f.feature_submittals.some((s) => s.material_tracking === true)
    );

    merged.feature_submittals = [{ material_tracking: allTrue }];
  }

  // Common links_as_source (by target_feature_instance.id)
  const targetIds = features.map(
    (f) => new Set(f.links_as_source.map((l) => l.target_feature_instance?.id))
  );
  merged.links_as_source = merged.links_as_source.filter((l) =>
    targetIds.every((set) => set.has(l.target_feature_instance?.id))
  );

  // Common links_as_target (by source_feature_instance.id)
  const sourceIds = features.map(
    (f) => new Set(f.links_as_target.map((l) => l.source_feature_instance?.id))
  );
  merged.links_as_target = merged.links_as_target.filter((l) =>
    sourceIds.every((set) => set.has(l.source_feature_instance?.id))
  );

  return merged;
};

export const initialTranslateEntityData = (
  features: TInputJSON[]
): TEntityLink | undefined => {
  const commonFeatures = findColumnDataOfFeature(features);
  if (commonFeatures) {
    const entityLink = mergeLinksRecursive(commonFeatures);
    entityLink.ids = features?.map((x) => x.id) || [entityLink.id];
    return entityLink;
  }
  return undefined;
};
